import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useDateFilterVersion } from "@/lib/useDateFilterVersion";
import { X } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import StockItemView from "@/pages/StockItemView";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtQ = (n) => (!n ? "" : Number(n).toLocaleString("en-IN"));
const fmtV = (n) => (!n ? "" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtR = (n) => (!n ? "" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export default function GroupSummaryView({ onClose }) {
  const { user } = useAuth();
  const company = user?.company_name || "";
  const [groups, setGroups] = useState([]);
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(0);
  const [summary, setSummary] = useState(null); // level 2
  const [drillItem, setDrillItem] = useState(""); // level 3 → StockItemView
  const dfv = useDateFilterVersion();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/stock-groups`);
        setGroups(res.data.groups || []);
      } catch {}
    })();
  }, [dfv]);

  const filtered = groups.filter((n) => n.toLowerCase().includes(query.toLowerCase()));

  const openGroup = useCallback(async (name) => {
    try {
      const res = await axios.get(`${API}/stock-group-summary`, { params: { group: name } });
      setSummary(res.data); setSel(0);
    } catch {}
  }, []);

  const back = useCallback(() => {
    if (summary) { setSummary(null); setSel(0); }
    else onClose();
  }, [summary, onClose]);

  // Date filter changed while a group summary is open -> refresh in place.
  useEffect(() => {
    if (dfv && summary) openGroup(summary.group);
  }, [dfv]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (drillItem) return;
    const onKey = (e) => {
      const tag = (e.target.tagName || "").toLowerCase();
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); back(); return; }
      const list = summary ? summary.rows : filtered;
      if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min(s + 1, list.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
      else if (e.key === "Enter" && (tag !== "input" || !summary)) {
        e.preventDefault();
        if (summary) { const r = summary.rows[sel]; if (r) setDrillItem(r.name); }
        else if (filtered[sel]) openGroup(filtered[sel]);
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [back, filtered, summary, sel, openGroup, drillItem]);

  if (drillItem) {
    return <StockItemView initialItem={drillItem} onClose={() => setDrillItem("")} />;
  }

  // ---------- Level 2: Stock Summary ----------
  if (summary) {
    const gt = summary.grand_total;
    return (
      <div className="w-full" data-testid="group-summary-report">
        <div className="w-full overflow-hidden shadow-xl flex flex-col" style={{ background: "#fbf7ee", border: "1px solid #e7dcc3", height: "calc(100vh - 0.5rem)" }}>
          <div className="flex items-center justify-between px-3 py-1.5 text-white" style={{ background: "#0f766e" }}>
            <span className="text-sm font-bold">Stock Summary</span>
            <span className="text-sm font-black uppercase tracking-wide">{company}</span>
            <button onClick={back} data-testid="gs-close" className="p-1 mr-12 hover:bg-white/20 rounded-sm">
              <X size={16} weight="bold" />
            </button>
          </div>
          <div className="flex justify-between items-end border-b-2 border-slate-700">
            <div className="px-4 py-2 text-sm font-bold tracking-[0.25em] text-slate-900">Particulars</div>
            <div className="text-right pr-2">
              <div className="text-sm italic font-semibold text-slate-800" data-testid="gs-group-name">{summary.group}</div>
              <div className="text-xs font-bold text-slate-900 uppercase">{company}</div>
              <div className="text-xs text-slate-700" data-testid="gs-period">{summary.period}</div>
              <div className="text-xs font-bold text-slate-900 border-t border-slate-400 mt-0.5">Closing Balance</div>
              <div className="flex text-[11px] font-semibold text-slate-700">
                <div className="w-[120px] text-right">Quantity</div>
                <div className="w-[90px] text-right italic">Rate</div>
                <div className="w-[120px] text-right">Value</div>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto pt-2" data-testid="gs-rows">
            {summary.rows.map((r, i) => (
              <div key={r.name} onMouseEnter={() => setSel(i)} onClick={() => setDrillItem(r.name)}
                className={`flex items-center cursor-pointer text-sm ${sel === i ? "bg-teal-600 text-white" : "text-slate-900 hover:bg-[#f1e9d6]"}`}
                data-testid={`gs-row-${i}`}>
                <div className="flex-1 px-4 py-0.5 font-semibold">{r.name}</div>
                <div className="w-[120px] px-1 py-0.5 text-right">{r.qty ? `${fmtQ(r.qty)} ${r.unit}` : ""}</div>
                <div className="w-[90px] px-1 py-0.5 text-right italic">{fmtR(r.rate)}</div>
                <div className="w-[120px] px-2 py-0.5 text-right">{fmtV(r.value)}</div>
              </div>
            ))}
            {summary.rows.length === 0 && (
              <div className="px-4 py-6 text-center text-xs text-slate-500">No stock items under this group</div>
            )}
          </div>
          <div className="flex items-center border-t-2 border-slate-700 bg-[#f1e9d6] text-sm font-black text-slate-900" data-testid="gs-grand-total">
            <div className="flex-1 px-4 py-1.5 tracking-[0.2em]">Grand Total</div>
            <div className="w-[120px] px-1 py-1.5 text-right">{fmtQ(gt.qty)}</div>
            <div className="w-[90px]" />
            <div className="w-[120px] px-2 py-1.5 text-right">{fmtV(gt.value)}</div>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Level 1: Select Group ----------
  return (
    <div className="flex justify-center items-start pt-4 pb-10 min-h-[70vh]" data-testid="group-summary-select">
      <div className="w-full max-w-md rounded-md overflow-hidden shadow-xl" style={{ background: "#eaf6ea", border: "1px solid #cfe4cf" }}>
        <div className="text-white text-center py-2 text-sm font-bold tracking-widest uppercase" style={{ background: "#0f766e" }}>
          Select Group
        </div>
        <div className="px-6 pt-4 pb-2">
          <div className="text-center text-sm font-bold text-slate-900 underline mb-1">Name of Group</div>
          <input autoFocus value={query} onChange={(e) => { setQuery(e.target.value); setSel(0); }}
            data-testid="gs-search"
            className="w-full px-3 py-1.5 border border-slate-400 rounded-sm text-sm bg-white"
            placeholder="Type to search…" />
        </div>
        <div className="text-white text-center py-1 text-xs font-bold tracking-widest uppercase" style={{ background: "#0f766e" }}>
          List of Stock Groups
        </div>
        <div className="max-h-[52vh] overflow-y-auto py-1" data-testid="gs-list">
          {filtered.map((n, i) => (
            <button key={n} onClick={() => openGroup(n)} onMouseEnter={() => setSel(i)}
              data-testid={`gs-group-${n}`}
              className={`w-full text-left px-4 py-0.5 text-sm font-semibold ${sel === i ? "bg-teal-600 text-white" : "text-slate-900 hover:bg-[#dcecdc]"}`}>
              {n === "Primary" ? "♦ Primary" : n}
            </button>
          ))}
          {filtered.length === 0 && <div className="px-4 py-3 text-xs text-slate-500 text-center">No stock groups found</div>}
        </div>
        <div className="h-1.5" style={{ background: "#0f766e" }} />
      </div>
    </div>
  );
}
